/**
 * Moteur isométrique : grille extensible, projection, tri en profondeur.
 *
 * Ce fichier ne connaît **ni PixiJS ni le DOM** — uniquement des mathématiques. C'est ce
 * qui le rend intégralement testable, et ce qui permettra de faire évoluer la projection
 * (îles de l'archipel, §15) sans toucher au rendu.
 *
 * Repère : l'origine du monde est le centre du losange de la case (0, 0). L'axe `col` part
 * vers la droite et le bas, l'axe `row` vers la gauche et le bas — la convention isométrique
 * habituelle, qui fait que les deux axes se rejoignent visuellement vers le bas de l'écran.
 */

import { halfHeight, halfWidth } from './tileGeometry';
import type { TileGeometry } from './tileGeometry';

/** Taille de la grille. Immuable : une extension produit une nouvelle grille (§6, terrains). */
export interface IsoGrid {
  readonly cols: number;
  readonly rows: number;
}

export interface GridCell {
  readonly col: number;
  readonly row: number;
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface WorldBounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly width: number;
  readonly height: number;
  readonly centerX: number;
  readonly centerY: number;
}

/** Grille de départ imposée par le §6 : 10 × 10. */
export const INITIAL_GRID_SIZE = 10;

export function createGrid(cols: number, rows: number): IsoGrid {
  if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols < 1 || rows < 1) {
    throw new Error(`Taille de grille invalide : ${cols}×${rows}`);
  }
  return { cols, rows };
}

/**
 * Agrandit (ou réduit) la grille. Utilisé par l'achat de terrain (§6) ; la taille reste
 * bornée à au moins 1 × 1 pour qu'une réduction ne puisse jamais produire une grille vide.
 */
export function resizeGrid(grid: IsoGrid, deltaCols: number, deltaRows: number): IsoGrid {
  return createGrid(Math.max(1, grid.cols + deltaCols), Math.max(1, grid.rows + deltaRows));
}

export function isInsideGrid(grid: IsoGrid, cell: GridCell): boolean {
  return cell.col >= 0 && cell.row >= 0 && cell.col < grid.cols && cell.row < grid.rows;
}

export function cellCount(grid: IsoGrid): number {
  return grid.cols * grid.rows;
}

/** Position du centre du losange d'une case, en coordonnées monde. */
export function cellToWorld(cell: GridCell, geometry: TileGeometry): WorldPoint {
  return {
    x: (cell.col - cell.row) * halfWidth(geometry),
    y: (cell.col + cell.row) * halfHeight(geometry),
  };
}

/**
 * Case située sous un point du monde — l'inverse exact de `cellToWorld`.
 *
 * Le losange étant la maille du pavage, arrondir les coordonnées continues suffit :
 * le point est attribué au centre le plus proche, ce qui correspond bien à la case
 * qui le contient. Aucun test d'appartenance à un polygone n'est nécessaire.
 *
 * La case retournée peut être hors grille : à l'appelant de filtrer avec `isInsideGrid`.
 */
export function worldToCell(point: WorldPoint, geometry: TileGeometry): GridCell {
  const u = point.x / halfWidth(geometry);
  const v = point.y / halfHeight(geometry);

  return {
    col: Math.round((v + u) / 2),
    row: Math.round((v - u) / 2),
  };
}

/**
 * Clé de tri en profondeur (ordre du peintre) : plus la valeur est faible, plus l'objet
 * est dessiné tôt, donc loin.
 *
 * Le bloc de terre descend d'un peu plus d'une rangée sous la face, si bien qu'une tuile
 * recouvre ses voisines de somme supérieure. Trier sur `col + row` est donc nécessaire —
 * et suffisant : deux cases de même somme sont côte à côte horizontalement et ne se
 * chevauchent jamais.
 */
export function depthOf(cell: GridCell): number {
  return cell.col + cell.row;
}

/**
 * Parcourt toutes les cases dans l'ordre de dessin (de la plus lointaine à la plus proche).
 */
export function forEachCellInDrawOrder(grid: IsoGrid, visit: (cell: GridCell) => void): void {
  const lastDepth = grid.cols + grid.rows - 2;

  for (let depth = 0; depth <= lastDepth; depth++) {
    const firstCol = Math.max(0, depth - grid.rows + 1);
    const lastCol = Math.min(depth, grid.cols - 1);
    for (let col = firstCol; col <= lastCol; col++) {
      visit({ col, row: depth - col });
    }
  }
}

/**
 * Rectangle englobant occupé par la grille en coordonnées monde, **bloc de terre inclus**.
 * Sert à la caméra pour borner le déplacement et calculer le zoom d'ajustement.
 */
export function gridWorldBounds(grid: IsoGrid, geometry: TileGeometry): WorldBounds {
  const hw = halfWidth(geometry);
  const hh = halfHeight(geometry);

  // Sommets extrêmes de la grille : (0,0) en haut, (cols-1,0) à droite,
  // (0,rows-1) à gauche, (cols-1,rows-1) en bas.
  const minX = -(grid.rows - 1) * hw - hw;
  const maxX = (grid.cols - 1) * hw + hw;
  const minY = -hh;
  const maxY = (grid.cols + grid.rows - 2) * hh + hh + geometry.blockHeight;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
}
